// Übungsquiz — freiwilliges Quiz ohne Wertung (BG-Teilnehmer der AZAV-Maßnahmen).
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Check, X, ArrowRight, RotateCcw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UebungsquizCard = ({ courseId, getAuthHeader }) => {
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/courses/${courseId}/uebungsquiz?count=10`, getAuthHeader());
      if (!res.data.questions?.length) { toast.info('Noch keine Übungsfragen für diesen Kurs'); setLoading(false); return; }
      setQuestions(res.data.questions);
      setIdx(0); setPicked(null); setScore(0); setFinished(false);
    } catch { toast.error('Übungsquiz konnte nicht geladen werden'); }
    setLoading(false);
  };

  const q = questions?.[idx];
  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.correct_index) setScore((s) => s + 1);
  };
  const next = () => {
    if (idx + 1 >= questions.length) setFinished(true);
    else { setIdx(idx + 1); setPicked(null); }
  };

  return (
    <aside className="mb-8 rounded-xl bg-cream-100 border border-cream-300" data-testid="uebungsquiz-card">
      <div className="px-5 sm:px-6 pt-5 pb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-terra/10 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-terra" strokeWidth={1.7} />
        </div>
        <div>
          <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">Freiwillig · Ohne Wertung</p>
          <h3 className="font-display text-xl text-espresso">Übungsquiz zum Unterricht</h3>
        </div>
      </div>

      {!questions && (
        <div className="px-5 sm:px-6 pb-6">
          <p className="text-[13.5px] text-sand-600 mb-4 leading-relaxed">
            Wiederhole die Inhalte aus dem Live-Online-Unterricht — 10 zufällige Fragen, sofortiges Feedback, keine Bewertung.
          </p>
          <button
            onClick={start}
            disabled={loading}
            className="inline-flex items-center gap-2 h-11 px-7 rounded-full bg-terra hover:bg-terra-dark text-white text-[13px] font-semibold transition-colors disabled:opacity-60"
            data-testid="uebungsquiz-start-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            10 Übungsfragen starten
          </button>
        </div>
      )}

      {questions && !finished && q && (
        <div className="px-5 sm:px-6 pb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand-500 mb-2">Frage {idx + 1} von {questions.length}</p>
          <p className="text-[15px] text-espresso font-semibold leading-snug mb-4" data-testid="uebungsquiz-question">{q.question}</p>
          <div className="space-y-2 mb-4">
            {q.options.map((opt, i) => {
              const isCorrect = picked !== null && i === q.correct_index;
              const isWrongPick = picked === i && i !== q.correct_index;
              return (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  className="w-full flex items-start gap-2.5 px-4 py-2.5 rounded-lg border text-left text-[13.5px] transition-colors"
                  style={isCorrect
                    ? { borderColor: '#8F650F', backgroundColor: 'rgba(216,146,50,0.12)', color: '#3E362E' }
                    : isWrongPick
                      ? { borderColor: '#E07A5F', backgroundColor: 'rgba(224,122,95,0.10)', color: '#3E362E' }
                      : { borderColor: '#E0D6CA', backgroundColor: '#FBF7F1', color: '#4A4238' }}
                  data-testid={`uebungsquiz-option-${i}`}
                >
                  {isCorrect && <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#8F650F' }} />}
                  {isWrongPick && <X className="w-4 h-4 mt-0.5 shrink-0 text-terra" />}
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
          {picked !== null && (
            <>
              {q.explanation && (
                <p className="text-[12.5px] text-sand-600 leading-relaxed mb-4 pl-3 border-l-2" style={{ borderColor: '#D89232' }}>
                  {q.explanation}
                </p>
              )}
              <button
                onClick={next}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark transition-colors"
                data-testid="uebungsquiz-next-btn"
              >
                {idx + 1 >= questions.length ? 'Ergebnis ansehen' : 'Nächste Frage'} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      )}

      {finished && (
        <div className="px-5 sm:px-6 pb-6 text-center">
          <p className="font-display text-3xl text-espresso mb-1" data-testid="uebungsquiz-result">{score} / {questions.length}</p>
          <p className="font-display italic text-[15px] text-sand-600 mb-4">
            {score >= questions.length * 0.8 ? 'Stark — du bist gut vorbereitet.' : 'Übung macht den Profi — gleich nochmal?'}
          </p>
          <button
            onClick={start}
            className="inline-flex items-center gap-2 h-10 px-6 rounded-full border border-terra text-terra hover:bg-terra hover:text-white text-[13px] font-semibold transition-colors"
            data-testid="uebungsquiz-retry-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Neue Runde üben
          </button>
        </div>
      )}
    </aside>
  );
};
